import { Bar } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "../../../AppProvider";
import { faLoader } from "../../../utilities/fontawesomeIcons";
import moment from "moment";
import { transformStringToColor } from "../../../utilities/utilities";
import { Carrier } from "../../../Models/Carrier";
import { ChartData } from "chart.js";
import { KpiShipmentPerformance } from "../../../Models/KpiShipmentPerformance";
import { useShipmentsPerformancesDelayedDaysGroupedContext } from "../providers/ShipmentsPerformancesDelayedDaysGrouped";
import { useInView } from "react-intersection-observer";
import { useMemo } from "react";

const DelayedDaysGroupedByCarrier = () => {
  const { dictionary } = useAppContext();
  const { data, loader, filters } =
    useShipmentsPerformancesDelayedDaysGroupedContext();
  const { ref, inView } = useInView({ triggerOnce: true });

  const formatChartData = (
    data: KpiShipmentPerformance[]
  ): ChartData<"bar"> => {
    const groupedData = data.reduce<Record<number, Record<string, number>>>(
      (acc, { partial, total, type }) => {
        const code = (type as Carrier).code;
        if (!acc[partial]) acc[partial] = {};
        acc[partial][code] = (acc[partial][code] || 0) + total;
        return acc;
      },
      {}
    );

    const carriers = Object.values(
      data.reduce<Record<string, Carrier>>((acc, { type }) => {
        const carrier = type as Carrier;
        acc[carrier.code] = carrier;
        return acc;
      }, {})
    );

    const labels = Object.keys(groupedData).map(Number);

    const datasetKeys = Array.from(
      new Set(data.map(({ type }) => (type as Carrier).code))
    );

    const datasets = datasetKeys.map((code) => ({
      label: carriers.find((c) => c.code === code)?.name || code,
      data: labels.map((label) => groupedData[label]?.[code] || 0),
      backgroundColor: transformStringToColor(code),
    }));

    return {
      labels,
      datasets,
    };
  };

  const barContent = useMemo(() => {
    if (!inView || !data?.length) return null;
    return (
      <Bar
        data={formatChartData(data)}
        options={{
          indexAxis: "y",
          plugins: {
            legend: {
              display: true,
            },
            tooltip: {
              callbacks: {
                title: (tooltipItems) => {
                  return `${tooltipItems[0].label} ${dictionary.times.days}`;
                },
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              min: 0,
              title: {
                display: true,
                text: dictionary.shipments.shipments,
              },
            },
            y: {
              beginAtZero: true,
              min: 0,
              title: {
                display: true,
                text: dictionary.times.working_days,
              },
              ticks: {
                display: true,
                autoSkip: false,
              },
            },
          },
        }}
      />
    );
  }, [inView, data]);

  return (
    <div ref={ref} className="box-container text-center h-100">
      <div className="row align-items-center mb-1">
        <div className="col">
          <label className="label-4">{dictionary.kpi.delay}</label>
          <label className="label-2">
            {moment(filters.start).isSame(moment(filters.end), "day")
              ? moment(filters.start).format("DD/MM/YYYY")
              : moment(filters.start).format("DD/MM/YYYY") +
                " - " +
                moment(filters.end).format("DD/MM/YYYY")}
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <label className="label-5">{dictionary.kpi.delay_desc}</label>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col">
          {loader ? (
            <FontAwesomeIcon icon={faLoader} spin size="4x" />
          ) : data?.length ? (
            barContent
          ) : (
            <label className="label-3">
              {dictionary.messages.no_results_found}
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export default DelayedDaysGroupedByCarrier;
